import { EditOutlined } from '@ant-design/icons';
import { Button } from '@mm/components';
import { useAudiencesCRUDActions } from '@mm/state/actions/AudiencesCRUDActions';
import { useContactsCRUDActions } from '@mm/state/actions/ContactsCRUDActions';
import { useViewActions } from '@mm/state/actions/viewActions';
import AudiencesModel, { TagFilterItem } from '@mm/state/model/AudiencesModel';
import { getFormattedContacts } from '@mm/state/modules/contactTableState/selectors';
import { useModalActions } from '@mm/state/modules/modalsState/actions';
import {
  selectContacts,
  selectContactsByAudienceId,
} from '@mm/state/selectors/contactsSelector';
import {
  activeChatbotIdSelector,
  connectedPageSelector,
} from '@mm/state/selectors/viewSelectors';
import { StoreState } from '@mm/state/store-interfaces';
import { Divider, Pagination, Tooltip } from 'antd';
import React, { useCallback, useState } from 'react';
import { useSelector } from 'react-redux';
import ContactsTable from '../../../../contacts/components/ContactsTable/ContactTable';
import { MyLeadsFilterBar } from '../../Contacts/MyLeadsFilterBar/MyLeadsFilterBar';
import {
  prepareEsFiltersStructure,
  unassignFilterParams,
} from '../../Contacts/MyLeadsFiltersPanel/helpers';
import { useCreateFilterParams } from '../../Contacts/use-create-filter-params';
import { IntegrationIcon } from '../../Integrations/IntegrationIcon/IntegrationIcon';
import { PAGE_SIZE } from '../Audiences';
import { ContactSlideoutMenuProps } from '../ContactSlideoutMenu/ContactSlideoutMenu';
import { useFetchAudienceContacts } from '../use-fetch-audience-contacts';
import styles from './Audience.module.css';
import { AudienceToolbarButtons } from './AudienceToolbarButtons';

// tslint:disable-next-line: cyclomatic-complexity
export const Audience = ({
  busy,
  activeAudienceId,
  audience: audienceProp,
  processClick,
}: {
  audience: AudiencesModel;
  activeAudienceId?: number;
  busy?: boolean;
  processClick: (onSuccess: (args: any) => void) => void;
}) => {
  const connectedPage = useSelector(connectedPageSelector);
  const activeChatbotId = useSelector(activeChatbotIdSelector);
  const activeAudience = useSelector(
    (state: StoreState) => state.audiences.audienceDetails,
  );

  const audience =
    activeAudience?.id === activeAudienceId ? activeAudience : audienceProp;

  const isElasticBasedAudience = !!audience && audience?.type === 'es_based';

  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');

  const toggleSortOrder = () => {
    setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
  };

  const { fetchPage: fetchContacts, isFetching } = useFetchAudienceContacts({
    activeAudienceId,
    isElasticBasedAudience,
    options: {
      pageSize: PAGE_SIZE,
      skipBusy: true,
      pauseRefreshingWhenEditingAudience: true,
      withDeliverable: true,
      sortColumn: 'created_at',
      sortDirection: sortOrder,
    },
  });

  const contactsCRUDActions = useContactsCRUDActions();
  const audiencesCRUDActions = useAudiencesCRUDActions();
  const { showSlideoutMenu } = useViewActions();
  const { openConversionURLModal } = useModalActions();

  const isPurchasedAudience = audience?.type === 'leads_db';

  const { filterParams, setFilterParams } = useCreateFilterParams({
    query: prepareEsFiltersStructure(audience?.esFilters) || {},
    filterParams: unassignFilterParams(audience?.filterParams),
    tagsFilters: unassignFilterParams(audience?.tagsFilters) as TagFilterItem[],
    page: 1,
  });

  const getContactsFromAudience = audience?.id;

  const onChangePagination = (pageNumber: number) => {
    fetchContacts({ pageSize, pageNumber });
  };

  const deleteSelectedContact = async (
    contactId: number,
    eraseRemoteMessages: boolean,
  ) => {
    if (connectedPage) {
      await contactsCRUDActions.deleteSelectedContact(
        connectedPage.id,
        contactId,
        pagination?.page,
        pagination?.perPage,
        undefined,
        eraseRemoteMessages,
      );

      await Promise.all([
        fetchContacts({ pageSize, pageNumber: pagination?.page }),
        audiencesCRUDActions.retrieveAll(activeChatbotId),
      ]);
    }
  };

  const getSelectedContact = (contactId: number) =>
    openContactSlideoutMenu({
      contactId,
      deleteSelectedContact,
      onContactSelect: getSelectedContact, // user might select associated contacts within contact
      onContactUpdateSuccess: () =>
        fetchContacts({ pageSize, pageNumber: pagination?.page }),
    });

  const openContactSlideoutMenu = useCallback(
    (props: ContactSlideoutMenuProps) => {
      showSlideoutMenu('contact', props);
    },
    [showSlideoutMenu],
  );

  const allContacts = useSelector(selectContacts);
  const contactsByAudienceId = useSelector(state =>
    selectContactsByAudienceId(state, audience?.id),
  );

  const contacts = getContactsFromAudience
    ? contactsByAudienceId?.contacts
    : allContacts.contacts;
  const pagination = getContactsFromAudience
    ? contactsByAudienceId?.pagination
    : allContacts.pagination;
  const pageSize = pagination?.perPage;
  const contactsCount = audience
    ? audience?.contactsCount
    : pagination?.total ?? 0;

  const usedInIntegrations = !!audience?.integrationTypes?.length;
  const usedInAutomationsCount = audience?.automations?.length;
  const isConvertedLeadsAudience = audience?.type === 'converted_leads';

  const onEditFiltersClick = useCallback(() => {
    if (isConvertedLeadsAudience) {
      openConversionURLModal({ audience, setFilterParams });
    } else {
      showSlideoutMenu('myLeadsFilters', {
        title: audience?.name,
        activeAudience: audience,
        filterParams,
        setFilterParams,
      });
    }
  }, [
    showSlideoutMenu,
    audience,
    filterParams,
    setFilterParams,
    isConvertedLeadsAudience,
    openConversionURLModal,
  ]);

  return (
    <>
      {(!!usedInAutomationsCount || usedInIntegrations) && (
        <div className={styles.statsInfo}>
          {usedInIntegrations && (
            <div className={styles.statsRow}>
              Integrations used with this audience:{' '}
              {audience.integrationTypes?.map(type => (
                <IntegrationIcon key={type} integrationType={type} isActive />
              ))}
            </div>
          )}
          {!!usedInAutomationsCount && (
            <Tooltip
              overlayClassName={styles.tooltip}
              placement="right"
              title={audience?.automations?.map(item => (
                <div key={item[0]} className={styles.automationName}>
                  {item[1]}
                </div>
              ))}
            >
              <div className={styles.statsRow}>
                Automations:
                <span style={{ fontWeight: 500 }}>
                  {usedInAutomationsCount}
                </span>
              </div>
            </Tooltip>
          )}
        </div>
      )}
      <Divider className={styles.divider} />

      <section className={styles.section}>
        <AudienceToolbarButtons
          processClick={processClick}
          audience={audience}
          contactsCount={contactsCount}
        />
        <div className={styles.filterBarContainer}>
          <div className={styles.filterBar}>
            <div className={styles.filterBarHeader}>
              <span className={styles.filterBarLabel}>Audience filters:</span>
            </div>

            <MyLeadsFilterBar
              filterParams={filterParams}
              setFilterParams={setFilterParams}
              tagColor=""
              closable={false}
              withReset={false}
              onBeforeTagsComponent={
                !isPurchasedAudience && (
                  <Button
                    color="transparent-no-shadow"
                    leftIcon={<EditOutlined />}
                    onClick={onEditFiltersClick}
                    css={{ container: styles.secondaryButton }}
                  >
                    Edit Filters
                  </Button>
                )
              }
            />
          </div>
        </div>

        <ContactsTable
          css={{
            wrapper: styles.contactsTableWrapper,
            container: styles.contactsTableContainer,
            rowsContainer: styles.contactsTableRowsContainer,
          }}
          hideTotal
          onChangePagination={onChangePagination}
          getSelectedContact={getSelectedContact}
          contacts={getFormattedContacts(contacts ?? [])}
          deleteSelectedContact={deleteSelectedContact}
          pagination={pagination}
          searchActive={busy || isFetching}
          disableSorting={!activeAudienceId || !isElasticBasedAudience}
          toggleSortOrder={toggleSortOrder}
        />
        <div className={styles.pagination}>
          <Pagination
            current={pagination?.page}
            onChange={onChangePagination}
            pageSize={pagination?.perPage}
            showSizeChanger={false}
            total={pagination?.totalPages}
            disabled={busy || isFetching}
            hideOnSinglePage
          />
        </div>
      </section>
    </>
  );
};
